import React, { FC, useEffect, useState } from "react";

import { LoadingSpinner } from "@xapp/components";

import {
    OpportunityAlertDetailType,
    UpdateAppInput,
    useAddChatWidgetChannelMutation,
    useAddFormWidgetChannelMutation,
    useGetWebsiteAnalysisQuery,
    UserProfile,
    useUpdateApplicationMutation,
    useUpdateEmailOpportunityAlertsMutation,
    useUpdateSearchChannelMutation, // can be used for adding or updated
    useUpdateStatusMutation
} from "generated/graphql";
import { ClientApp } from "models";
import { wrapUrqlMutation } from "utils/graphql";
import { getProducts } from "utils/getProducts";

import { OnboardingProps } from "../models";
import { analyzeWebsiteData } from "../utils/analyzeWebsiteData";
import { buildDefaultChatTheme } from "utils/generateCreateAppWidgetInput";
import { buildDefaultSearchTheme } from "components/SearchWidgetThemeBuilder/SearchWidgetThemeBuilder";
import { buildDefaultFormTheme } from "utils/generateFormWidgetInput";
import { APP_STATUS_READY_FOR_INSTALL } from "stentor-models";

export interface FinalizeContainerProps {
    style?: React.CSSProperties;
    className?: string;
    /**
     * Profile of the user
     */
    profile: UserProfile;

    onboardingProps: OnboardingProps;
    /**
     * The user is ready for the next step
     * @param app
     */
    onNextStep(app: Pick<ClientApp, "appId" | "organizationId">): void;
}

/**
 * Looks at the data gathered from web analysis/executionId and updates based on received information.
 *
 * There is no UI the user can interact with, this is simply and updating step.
 *
 * It will:
 * - Update businessDescription
 * - Update leadsContact
 * - Update theme colors on widgets
 * - Update beta information with web data
 */
export const FinalizeContainer: FC<FinalizeContainerProps> = props => {

    const { onNextStep, onboardingProps, profile } = props;
    const { productId } = onboardingProps;

    const appId: string = onboardingProps.appId;

    const [error, setError] = useState<{ error: string, message: string }>();

    // Website Analysis Related
    const [creating, setCreating] = useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = useState<string>();
    const [executionId, setExecutionId] = useState<string>(onboardingProps.executionId);
    const [propsFromSite, setPropsFromSite] = useState<OnboardingProps>();

    useEffect(() => {
        if (onboardingProps.executionId && !propsFromSite) {
            setExecutionId(onboardingProps.executionId);
        }
    }, [propsFromSite, onboardingProps.executionId]);

    const products = getProducts();
    const product = products.find((product) => product.id === productId);

    const [getAnalysis, executeGetAnalysis] = useGetWebsiteAnalysisQuery({
        pause: !executionId,
        requestPolicy: "cache-and-network",
        variables: {
            executionId
        }
    });

    const { data: analysisData } = getAnalysis;

    useEffect(() => {
        // clear it out if it is complete, and we have an executionId (which prevents us from running this again)
        if (executionId && analysisData?.onboarding?.webDetailsExecution?.completed) {
            const partialApp: OnboardingProps = analyzeWebsiteData(analysisData.onboarding.webDetailsExecution.siteCrawled, analysisData, props?.onboardingProps?.leadsContact);

            setExecutionId(undefined);
            setPropsFromSite(partialApp);
            // clear out executionId
        }
    }, [analysisData, executionId, onNextStep, props?.onboardingProps?.leadsContact])

    useEffect(() => {
        // this polls the executeGetAnalysis query until it is done.
        if (executionId && !getAnalysis.fetching && !propsFromSite) {
            setLoadingMessage("Fetching latest data...");
            const id = setTimeout(() => {
                executeGetAnalysis({ requestPolicy: 'network-only' });
            }, 2000);
            setLoadingMessage(undefined);
            return () => {
                clearTimeout(id)
            }
        }
    }, [executionId, getAnalysis.fetching, executeGetAnalysis, propsFromSite]);

    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the contact information on the app for you." });
            return { send: false }
        }
    });

    const updateStatus = wrapUrqlMutation(useUpdateStatusMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages

            //    const error = errors.join(", ");
            //   setError({ error, message: "We're sorry, we had trouble updating the status for you." });
            return { send: false }
        }
    });

    const updateEmailOpportunityAlerts = wrapUrqlMutation(useUpdateEmailOpportunityAlertsMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating an opportunity alert for you." });
            return { send: false }
        }
    });

    const updateWidget = wrapUrqlMutation(useAddChatWidgetChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the widget channel for you." });
            return { send: false }
        }
    });

    const updateSearch = wrapUrqlMutation(useUpdateSearchChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating a search widget for you." });
            return { send: false }
        }
    });

    const updateForm = wrapUrqlMutation(useAddFormWidgetChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the scheduler channel for you." });
            return { send: false }
        }
    });

    useEffect(() => {

        // Performs final steps after checkout
        const finalize = async (createProps: OnboardingProps, propsFromSite?: OnboardingProps) => {

            console.log('START FINALIZING');
            console.log('createProps', createProps);
            console.log('propsFromSite', propsFromSite);
            // this prevents us from running this method again
            setCreating(true);

            const appId = createProps.appId;
            const organizationId: string = createProps.organizationId;


            /* TODO: Update contact capture handler no capture start
            const contactCaptureHandler = handlers.find((handler) => {
                return handler.intentId === "LeadGeneration";
            });

            const contactCaptureResponses = contactCaptureHandler.content.find((content) => {
                return content.key === "LeadGeneration";
            });

            contactCaptureResponses.handlerResponse.push({
                outputSpeech: {
                    ssml: `<speak>Sure, please contact us at ${createProps.leadsContactPhone} or ${createProps.leadsContact}.</speak>`,
                    displayText: `Sure, please contact us at ${createProps.leadsContactPhone} or ${createProps.leadsContact}.`,
                    suggestions: []
                },
                name: "No Capture Start",
                tag: "ContactCaptureNoCaptureStart"
            });

            const mainFormName = "scheduling_form_v1";
            // add the form metadata
            contactCaptureHandler.data["CAPTURE_MAIN_FORM"] = mainFormName;
            const mainForm = generateFormsData({
                name: mainFormName,
                options: [{
                    type: "CONTACT_US",
                    label: "Contact Us"
                }]
            });
            contactCaptureHandler.data.forms = [mainForm];
            */

            // if we have webProps, we will use them to update the app
            // updates app & channels
            if (propsFromSite) {
                createProps.businessDescription = propsFromSite.businessDescription;
                createProps.logo = propsFromSite.logo;
                createProps.primaryColor = propsFromSite.primaryColor;
                createProps.secondaryColor = propsFromSite.secondaryColor;
                createProps.technologies = propsFromSite.technologies;
                // TODO: we should ask for this on the place!
                createProps.leadsContactPhone = propsFromSite.leadsContactPhone;
                createProps.cms = propsFromSite.cms;
                createProps.callToAction = propsFromSite.callToAction;

                setLoadingMessage("Updating contact information...");
                await new Promise(r => setTimeout(r, 200));

                const updateAppInput: UpdateAppInput = {
                    businessDescription: createProps.businessDescription,
                    icon: createProps.logo,
                    leadsContact: createProps.leadsContact,
                    leadsContactPhone: createProps.leadsContactPhone,
                    // website data!
                    websiteData: {
                        crawlExecutionId: createProps.executionId,
                        cms: createProps.cms,
                        primaryColor: createProps.primaryColor,
                        secondaryColor: createProps.secondaryColor,
                        callsToAction: createProps.callToAction ? [createProps.callToAction] : [],
                    },
                }

                await updateApplication({
                    appId,
                    app: updateAppInput
                });

                if (propsFromSite.primaryColor) {
                    setLoadingMessage(`Updating theme color ${propsFromSite.primaryColor}`);
                    await new Promise(r => setTimeout(r, 200));

                    const chatTheme = buildDefaultChatTheme(createProps.primaryColor);

                    await updateWidget({
                        appId,
                        channel: {
                            id: createProps.chatWidgetId,
                            type: "chat-widget",
                            theme: chatTheme
                        }
                    });

                    const searchTheme = buildDefaultSearchTheme(createProps.primaryColor);
                    await new Promise(r => setTimeout(r, 200));

                    await updateSearch({
                        appId,
                        channel: {
                            id: createProps.searchWidgetId,
                            type: "intelligent-search",
                            theme: searchTheme
                        }
                    });

                    const formTheme = buildDefaultFormTheme(createProps.primaryColor);
                    await new Promise(r => setTimeout(r, 200));

                    await updateForm({
                        appId,
                        channel: {
                            type: "form-widget",
                            id: createProps.formWidgetId,
                            theme: formTheme
                        }
                    });
                }
            }

            const supportsLiveChat = product?.metadata?.['live-chat'] === "true";

            if (supportsLiveChat && createProps.leadsContact) {
                setLoadingMessage("Setting up opportunity alerts for live chat...");
                await new Promise(r => setTimeout(r, 200));
                await updateEmailOpportunityAlerts({
                    appId,
                    alert: {
                        email: createProps.leadsContact,
                        alerts: [
                            {
                                duringBusinessHours: true,
                                outsideBusinessHours: true,
                                type: OpportunityAlertDetailType.NewSession
                            }
                        ]
                    }
                })
            }

            await new Promise(r => setTimeout(r, 200));
            // Update status
            await updateStatus({
                appId,
                type: APP_STATUS_READY_FOR_INSTALL,
                notes: `Finalized ${new Date().toDateString()} by ${profile.email} during onboarding.`
            });

            setLoadingMessage(`Redirecting to your new AI....`);

            // Delay for 1.5 s
            await new Promise(r => setTimeout(r, 200));
            // Move on!
            onNextStep({ appId, organizationId });
        };

        if (creating) {
            return;
        }

        // we can potentially get new onboardingProps that are just an empty object
        // this is why we check for keys
        if (onboardingProps && Object.keys(onboardingProps).length > 0) {
            console.log('we have onboarding props');
            console.log(JSON.stringify(onboardingProps, undefined, 2));
            // if we do have props we have two paths, one with an executionId and one without
            if (onboardingProps.executionId) {
                // executionId we look to see if we have site data yet
                if (propsFromSite) {
                    finalize(onboardingProps, propsFromSite);
                } else {
                    console.log('NOT finalizing, no appfromsite yet');
                }
            } else {
                // otherwise, not executionId, we just finalize
                finalize(onboardingProps);
            }
        } else {
            console.log('NOT finalizing, no onboardingprops');
        }

    }, [propsFromSite, executionId, onNextStep, onboardingProps, updateStatus, profile.email, updateApplication, updateWidget, updateSearch, updateForm, updateEmailOpportunityAlerts, creating, appId, product?.metadata]);

    return (
        <div style={props.style} className={props.className}>
            {loadingMessage && <LoadingSpinner loadingText={loadingMessage} />}
            {propsFromSite && propsFromSite.primaryColor ? (
                <div>
                    {propsFromSite.primaryColor && <div style={{ backgroundClip: propsFromSite.primaryColor, height: "20px", width: "20px" }} />}
                    {propsFromSite.secondaryColor && <div style={{ backgroundClip: propsFromSite.secondaryColor, height: "10px", width: "20px" }} />}
                </div>
            ) : <></>}
            {error && <div>{error.message}</div>}
        </div>
    );
}